/* Common Imports */

import { styled, useMediaQuery } from "@mui/system";
import React from "react";

/* Redux Imports */

import StateInterface from "../../../../redux-magic/state-interface";
import { profile_context as emptyProfileContext } from "../../../../redux-magic/sub-state-data-empty/profile-context";
import {
	profileContextThunk,
	setProfileContextThunk,
	updateCurrencyThunk,
	updateReduxSessionThunk,
} from "../../../../redux-magic/thunks";

/* Component Imports */

import {
	FormControlLabel,
	Link,
	Radio,
	RadioGroup,
	Skeleton,
	SvgIcon,
	SwipeableDrawer,
	Switch,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import ColorModeContext from "../../contexts/colorModeContext";

/* Icon Imports */

import {
	DarkMode,
	ExpandMore,
	Language,
	LightMode,
	LiveHelpOutlined,
	Logout,
	SettingsOutlined,
} from "@mui/icons-material";
import PremiumBadge from "../../../../public/images/badges/premium.svg";
import VerifiedPremiumBadge from "../../../../public/images/badges/verified_premium.svg";

/* Authentication Imports */

import { signOut } from "next-auth/react";

/* Routing Imports */

import { useRouter } from "next/router";
import { createLoginFlow, LoginFlowTriggers } from "../../../../lib/auth/helpers";
import BottomBuyBeegruPointsModal from "../../../modals/BottomBuyBeegruPointsModal";
import BuyBeegruPointsModal from "../../../modals/BuyBeegruPointsModal";

/* Styled Components */

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	padding: "1rem",
}));

const ContentInnerContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "1rem 1.25rem 1rem 1.25rem",
}));

const ContentRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "0.9rem",
	fontWeight: 400,
}));

const Text = styled(Typography)(({ theme }) => ({
	width: "calc(100% - 2rem)",
	fontSize: "0.875rem",
	fontWeight: 400,
	lineHeight: "1.5rem",
	textOverflow: "ellipsis",
}));

const Links = styled(Link)(({ theme }) => ({
	fontSize: "0.8rem",
	fontWeight: 400,
	lineHeight: "1.5rem",
	textDecoration: "none",
	color: theme.palette.mode == "dark" ? "#fff" : "#000000",
}));

const ProfilePicture = styled("img")(({ theme }) => ({
	width: "2.65rem",
	height: "2.65rem",
	borderRadius: "8px",
	objectFit: "cover",
	border: `1px solid ${theme.palette.divider}`,
}));

const LoadingContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	padding: "1rem",
}));

const ToolTipDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
}));

const CustomSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
	"& .MuiDrawer-paper": {
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		alignItems: "space-between",
		padding: "0rem",
		gap: "0rem",
		width: "100%",
	},
}));

// const UnordredList = styled("ul")(({ theme }) => ({
//   margin: 0,
//   paddingLeft: "1.5rem",
// }));

const Accordion = styled((props: AccordionProps) => (
	<MuiAccordion
		disableGutters
		elevation={0}
		square
		{...props}
	/>
))(({ theme }) => ({
	// border: `1px solid ${theme.palette.divider}`,
	// "&:not(:last-child)": {
	//   borderBottom: 0,
	// },
	"&:before": {
		display: "none",
	},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
	<MuiAccordionSummary
		expandIcon={
			<ExpandMore
				sx={{
					fontSize: "1.5rem",
					margin: "0rem 0rem 0rem 0rem",
					color: "#F6802A",
				}}
			/>
		}
		{...props}
	/>
))(({ theme }) => ({
	border: "none",
	minHeight: "auto",
	background: theme.palette.background.paper,
	flexDirection: "row",
	padding: "0rem 0rem 0rem 0rem",
	"& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
		transform: "rotate(-180deg)",
	},
	"& .MuiAccordionSummary-content": {
		margin: "0rem 0rem 0rem 0.3rem",
	},
	"& .MuiAccordionSummary-content p": {
		fontSize: "0.875rem",
		fontWeight: 400,
		lineHeight: "1.5rem",
	},
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	padding: "0rem 0rem 0rem 1rem",
	fontSize: "0.9rem",
}));

const CustomFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
	width: "100%",
	marginRight: "0rem",
	"& .MuiRadio-root": {
		width: "1rem",
		height: "1rem",
		margin: "0.4rem 0.6rem 0.4rem 0rem",
	},
}));

const TypeWithNameContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
}));

const TypeWithNameLink = styled(Link)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	textDecoration: "none",
	color: theme.palette.mode == "dark" ? "#fff" : "#000000",
	"&:hover": {
		color: theme.palette.primary.main,
	},
}));

const NameBadgeContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.5rem",
	width: "100%",
}));

const TypeTypo = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.25rem",
	alignItems: "center",
	width: "100%",
	fontWeight: "400",
	fontSize: "0.8rem",
	color: theme.palette.mode == "dark" ? "#D3D3D3" : "#36454F",
}));

const RadioWithLink = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0rem",
	padding: "0.25rem 0rem 0.25rem 0rem",
}));

const SubDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	gap: "0.35rem",
	overflow: "hidden",
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.25rem",
}));

const BeegruPointsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.5rem",
}));

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
	width: 62,
	height: 34,
	padding: 7,
	"& .MuiSwitch-switchBase": {
		margin: 1,
		padding: 0,
		transform: "translateX(6px)",
		"&.Mui-checked": {
			color: "#FFFFFF",
			transform: "translateX(22px)",
			"& .MuiSwitch-thumb:before": {
				backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
					theme.palette.primary.main,
				)}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
			},
			"& + .MuiSwitch-track": {
				opacity: 1,
				backgroundColor: theme.palette.secondary.dark,
			},
		},
	},
	"& .MuiSwitch-thumb": {
		backgroundColor: theme.palette.background.paper,
		width: 32,
		height: 32,
		border: `1px solid ${theme.palette.primary.main}`,
		borderRadius: "12px",
		boxShadow: "none",
		"&:before": {
			content: "''",
			position: "absolute",
			width: "100%",
			height: "100%",
			left: 0,
			top: 0,
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
				theme.palette.primary.main,
			)}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
		},
	},
	"& .MuiSwitch-track": {
		opacity: 1,
		backgroundColor: theme.palette.mode === "dark" ? "#333" : "#AAB4BE",
		borderRadius: "16px",
	},
}));

const UserContent = ({
	session,
	dispatch,
	profilePicture,
	profile_context,
	new_business_profile_url,
	redux_session_updated,
	bottom_buy_subscription,
	UserprofilePicture,
	login_flow_global_state,
	sign_out_trigger,
}: {
	session: any;
	dispatch: Function;
	profilePicture: string;
	profile_context: StateInterface["profile_context"];
	new_business_profile_url: StateInterface["new_business_profile_url"];
	redux_session_updated?: boolean;
	bottom_buy_subscription?: StateInterface["bottom_subscribe_buy_beegru_points"];
	UserprofilePicture: any;
	login_flow_global_state?: StateInterface["login_flow_global_state"];
	sign_out_trigger?: Function;
}) => {
	const router = useRouter();
	const theme = useTheme();

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const [openbasic, setOpenbasic] = React.useState(false);

	const [openPostMenu, setOpenPostMenu] = React.useState<boolean>(false);

	// const signIn = () => {
	// 	window.location.href = "/" + router.locale + "/auth/login?callbackUrl=" + encodeURIComponent(window.location.href);
	// };

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	const handleOpen = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
		e.stopPropagation();
		if (smDevice) {
			setOpenPostMenu(true);
		} else {
			setOpenbasic(!openbasic);
		}
	};

	const onClose = () => {
		setOpenPostMenu(false);
	};

	const handleClose = () => {
		setOpenbasic(false);
	};

	const [open, setOpen] = React.useState(false);
	const pathname = React.useRef(router).current;

	React.useEffect(() => {
		if (pathname.query.create_business_profile === "true" && redux_session_updated === true) {
			redux_session_updated === true && session && session.authorized ? setOpen(true) : signIn();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [redux_session_updated]);

	React.useEffect(() => {
		if (pathname.query.create_business_profile === "true" && redux_session_updated === true && open === false) {
			router.push("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const [loading, setLoading] = React.useState(false);
	const [openProfileContextSnackbar, setOpenProfileContextSnackbar] = React.useState(false);
	const [expanded, setExpanded] = React.useState<boolean>(true);
	// const [showMore, setShowMore] = React.useState<Boolean>(false);

	// const [countrySelect, setcountrySelect] = React.useState("india");
	// const [langaugeSelect, setLangaugeSelect] = React.useState("");
	// const [currency, setCurrency] = React.useState("");

	let businessProfiles: Array<any> = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.pages
					? session.user.dbUser.pages
					: []
				: []
			: []
		: [];

	// let businessProfilesCount = session
	// 	? session.user
	// 		? session.user.dbUser
	// 			? session.user.dbUser.business_profiles_count
	// 				? session.user.dbUser.business_profiles_count
	// 				: 0
	// 			: 0
	// 		: 0
	// 	: 0;

	const session_user = session ? (session.user ? session.user.dbUser : "") : "";

	const [radio, setRadio] = React.useState(
		profile_context && profile_context.is_business_profile
			? profile_context && profile_context.business_profile_id
			: profile_context && profile_context.user_id,
	);

	/* Create new business profile modal functions */

	// const handleOpenBusinessProfile = () => {
	// 	setOpen(!open);
	// };

	const handleCloseBusinessProfile = () => {
		setOpen(false);
	};

	/* Change profile context function */

	const onChangeProfileContext = async (id: string) => {
		setLoading(true);
		await dispatch(
			setProfileContextThunk({
				is_business_profile: id === session_user._id ? false : true,
				user_id: session_user ? session_user._id : "",
				business_profile_id: id,
				page_type:
					id === session_user._id ? "user" : businessProfiles.find((item: any) => item.page_id === id).page_type,
				user_kyc_status: session.user.dbUser.kyc_info.kyc_status,
				user_subscription_information: session.user.dbUser.subscription_information,
				remember_choice: true,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		);
		setLoading(false);
		setOpenProfileContextSnackbar(true);
	};

	/* Show more business profiles */

	// const handleClickShowMoreBusinessProfiles = async () => {
	// 	setLoading(true);
	// 	await dispatch(
	// 		FetchMoreBusinessProfilesThunk({
	// 			user_id: session_user ? session_user._id : "",
	// 			pages: businessProfiles.map((page: any) => {
	// 				return { page_id: page.page_id };
	// 			}),
	// 		}),
	// 	);
	// 	setLoading(false);
	// };

	const typeOfProfile = businessProfiles
		? businessProfiles.find((item: any) => item.page_id === radio) &&
			businessProfiles.find((item: any) => item.page_id === radio).page_type
		: "";

	const typeOfProfileSlug = businessProfiles
		? businessProfiles.find((item: any) => item.page_id === radio) &&
			businessProfiles.find((item: any) => item.page_id === radio).page_slug
		: "";

	// const userName =
	// 	session.user.dbUser.first_name !== ""
	// 		? session.user.dbUser.first_name + " " + session.user.dbUser.last_name
	// 		: session.user.dbUser.phone;

	React.useEffect(() => {
		setRadio(
			profile_context && profile_context.is_business_profile
				? profile_context && profile_context.business_profile_id
				: profile_context && profile_context.user_id,
		);
	}, [profile_context]);

	/* Business profile types */

	// let bp_select =
	// 	profile_context && profile_context.is_business_profile
	// 		? businessProfiles.find((item: any) => item.page_id === radio) &&
	// 			businessProfiles.find((item: any) => item.page_id === radio)
	// 		: "user";

	const localLang = {
		go_to_dashboard:
			router.locale === "ar-AE"
				? "اذهب إلى لوحة التحكم"
				: router.locale === "kn-IN"
					? "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಹೋಗಿ"
					: "Go to dashboard",
		go_to_profile:
			router.locale === "ar-AE"
				? "اذهب إلى الملف الشخصي"
				: router.locale === "kn-IN"
					? "ಪ್ರೊಫೈಲ್‌ಗೆ ಹೋಗಿ"
					: "Go to profile",
	};

	/* user redirect link  */
	const default_screen = session?.user?.dbUser?.platform_access_level?.default_screen;
	// const screenParam = default_screen && default_screen !== "home" ? `?screen=${default_screen}` : "";
	const profileUrl =
		profile_context && profile_context.is_business_profile
			? `/${router.locale}/${typeOfProfile}s/${typeOfProfileSlug}`
			: `/${router.locale}/users/${session?.user?.dbUser?.slug || session?.user?.dbUser?._id || ""}`;

	const account_settings_url =
		profile_context && profile_context.is_business_profile
			? `/${router.locale}/${typeOfProfile}s/${typeOfProfileSlug}?screen=settings`
			: `/${router.locale}/users/${session?.user?.dbUser?.slug || session?.user?.dbUser?._id || ""}/account-settings`;

	const loadingUrl = `${process.env.PRODUCTION_URL}loading.html?callbackUrl=${encodeURIComponent(profileUrl)}`;

	/* state to handle language change */

	const [expandedLanguage, setExpandedLanguage] = React.useState(false);
	const [expandedCurrency, setExpandedCurrency] = React.useState(false);

	const [radioLanguage, setRadioLanguage] = React.useState(router.locale);

	// const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
	// 	const { pathname, asPath, query } = router;
	// 	router.push({ pathname, query }, asPath, { locale: event.target.value });
	// };

	const handleLanguageUrl = (locale: string) => {
		const baseUrl = process.env.PRODUCTION_URL + locale + router.asPath;
		const loadingUrl = `${process.env.PRODUCTION_URL}/loading.html?callbackUrl=${encodeURIComponent(baseUrl)}`;

		window.location.href = loadingUrl;
	};

	const user_id = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const name = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.first_name
					? session.user.dbUser.first_name
					: "new" + "" + session.user.dbUser.last_name
						? session.user.dbUser.last_name
						: "user"
				: ""
			: ""
		: "";

	const country_code = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.country_code
				: ""
			: ""
		: "";

	const phone = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.phone
					? session.user.dbUser.phone
					: ""
				: ""
			: ""
		: "";

	const email = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.recovery_email
					? session.user.dbUser.recovery_email.email
					: ""
				: ""
			: ""
		: "";

	const available_beegru_points = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser.beegru_points
					? session.user.dbUser.beegru_points
					: 0
				: 0
			: 0
		: 0;

	return (
		<React.Fragment>
			{/* Buy Beegru Point Mobile view modal */}

			<CustomSwipeableDrawer
				anchor="bottom"
				open={openPostMenu}
				onClose={onClose}
				onOpen={() => setOpenPostMenu(true)}
				swipeAreaWidth={156}
				disableSwipeToOpen={true}
				ModalProps={{
					keepMounted: true,
				}}
				sx={{
					"& .MuiDrawer-paper": {
						background: theme.palette.background.paper,
						height: "100%",
						zIndex: 1500,
					},
					zIndex: 1500,
				}}
			>
				<BottomBuyBeegruPointsModal
					name={name}
					email={email}
					phone={phone}
					user_id={user_id}
					open={openbasic}
					onClose={onClose}
					bottom_buy_subscription={bottom_buy_subscription}
					dispatch={dispatch}
					listing_tabValue={0}
					available_beegru_points={Number(available_beegru_points?.toFixed(2))}
				/>
			</CustomSwipeableDrawer>

			{/* Buy beegru point modal */}

			<BuyBeegruPointsModal
				user_id={user_id}
				name={name}
				email={email}
				phone={"+" + country_code + phone}
				open={openbasic}
				onClose={handleClose}
				bottom_buy_subscription={bottom_buy_subscription}
				dispatch={dispatch}
				available_beegru_points={available_beegru_points}
			/>

			{/* User Profile */}

			<Links
				href={loadingUrl}
				rel="noopener"
				target="_self"
				referrerPolicy="no-referrer"
			>
				{loading ? (
					<ContentContainer
						sx={{
							borderBottom: "1px solid #0000001F",
							display: "flex",
							flexDirection: "row",
							justifyContent: "flex-start",
							alignItems: "center",
							gap: "1rem",
						}}
					>
						<Skeleton
							variant="circular"
							width={40}
							height={40}
							sx={{ borderRadius: "0.25rem" }}
						/>
						<div>
							<Skeleton
								variant="text"
								width={100}
								height={20}
								sx={{ borderRadius: "0.25rem" }}
							/>
							<Skeleton
								variant="text"
								width={150}
								height={20}
								sx={{ borderRadius: "0.25rem" }}
							/>
						</div>
					</ContentContainer>
				) : (
					<ContentContainer
						sx={{
							borderBottom: "1px solid #0000001F",
							display: "flex",
							flexDirection: "row",
							justifyContent: "flex-start",
							alignItems: "center",
							gap: "1rem",
						}}
					>
						{!profilePicture.includes("/images/icons/Avatar.svg") ? (
							<ProfilePicture
								src={`/profile-picture/${profilePicture}`}
								alt="user"
								referrerPolicy="no-referrer"
							/>
						) : (
							<ProfilePicture
								src={profilePicture}
								alt="user"
								referrerPolicy="no-referrer"
							/>
						)}

						<TypeWithNameContainer>
							<NameBadgeContainer>
								<Heading
									variant="h6"
									sx={{ fontSize: "1rem" }}
								>
									{profile_context
										? profile_context.is_business_profile
											? businessProfiles.find((item: any) => item.page_id === radio)
												? businessProfiles.find((item: any) => item.page_id === radio).page_title
												: ""
											: session
												? session.user
													? session.user.dbUser
														? session.user.dbUser.first_name
															? session.user.dbUser.first_name + " " + session.user.dbUser.last_name
															: session.user.dbUser.phone
														: ""
													: ""
												: ""
										: ""}
								</Heading>

								{profile_context ? (
									profile_context.kyc_info?.kyc_status !== "kyc_completed" &&
									profile_context.subscription_information &&
									profile_context.subscription_information.active ? (
										<Tooltip title="Premium">
											<ToolTipDiv>
												<SvgIcon
													component={PremiumBadge}
													inheritViewBox={false}
													viewBox="0 0 24 24"
													width={24}
													height={24}
													sx={{
														width: "0.95rem",
														height: "0.95rem",
													}}
												/>
											</ToolTipDiv>
										</Tooltip>
									) : profile_context.kyc_info?.kyc_status === "kyc_completed" &&
									  profile_context.subscription_information &&
									  profile_context.subscription_information.active ? (
										<Tooltip title="Verified Premium">
											<ToolTipDiv>
												<SvgIcon
													component={VerifiedPremiumBadge}
													inheritViewBox={false}
													viewBox="0 0 24 24"
													width={24}
													height={24}
													sx={{
														width: "0.95rem",
														height: "0.95rem",
													}}
												/>
											</ToolTipDiv>
										</Tooltip>
									) : null
								) : null}
							</NameBadgeContainer>
							{/* <Heading sx={{ fontSize: "0.8rem", color: "#666666" }}>{country_code + " " + phone}</Heading> */}
							{/* <TypeTypo>{BusinessProfileTypeString(profile_context.page_type, router)}</TypeTypo> */}
							<Heading
								sx={{
									fontSize: "0.8rem",
									fontWeight: 500,
									color: "#B05911",
									// textDecoration: "underline",
								}}
							>
								{profile_context?.is_business_profile ? localLang.go_to_dashboard : localLang.go_to_profile}
							</Heading>
						</TypeWithNameContainer>
					</ContentContainer>
				)}
			</Links>

			{/* {profile_context?.is_business_profile ? (
				<ContentInnerContainer onClick={() => (window.location.href = `${loadingUrl}?screen=transactions`)}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-start",
							alignItems: "flex-start",
							gap: "0.25rem",
						}}
					>
						<TextContainer>
							<Typography
								variant="subtitle2"
								sx={{
									padding: "0rem 0rem 0rem 0rem",
									color: "#666666",
									fontSize: "0.875rem",
									lineHeight: "1.35rem",
								}}
							>
								{router.locale === "ar-AE"
									? "نقاط بيجرو"
									: router.locale === "kn-IN"
										? "ಬೀಗ್ರು ಪಾಯಿಂಟ್"
										: "Beegru Points"}
							</Typography>
						</TextContainer>

						<BeegruPointsContainer>
							<SvgIcon
								component={Points}
								inheritViewBox={false}
								viewBox="0 0 24 24"
								width={24}
								height={24}
								sx={{
									width: "1.25rem",
									height: "1.25rem",
								}}
							/>
							<Text
								variant="h6"
								sx={{ fontSize: "1.25rem" }}
							>
								{session
									? session.user
										? session.user.dbUser.beegru_points
											? session.user.dbUser.beegru_points.toFixed(1)
											: 0
										: 0
									: 0}
							</Text>
						</BeegruPointsContainer>
					</div>

					<BeegruButton
						variant="contained"
						size="medium"
						sx={{
							"&.MuiButton-contained": {
								background: "rgba(252, 128, 25, 0.16)",
								color: "#B05911",
								"&:hover": {
									background: "rgba(252, 128, 25, 0.16)",
									color: "#B05911",
								},
							},
						}}
						onClick={handleOpen}
					>
						{router.locale === "ar-AE" ? "اشترِ المزيد" : router.locale === "kn-IN" ? "ಹೆಚ್ಚು ಖರೀದಿಸಿ" : "Buy more"}
					</BeegruButton>
				</ContentInnerContainer>
			) : null} */}

			{/* Dark Mode Switch */}

			{session?.user?.dbUser?.access_level.includes("super_admin") ||
			session?.user?.dbUser?.access_level.includes("admin") ? (
				<ContentContainer
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						alignItems: "center",
						padding: "1rem 1rem 1rem 0.8rem",
						gap: "0.5rem",
					}}
				>
					{theme.palette.mode === "dark" ? <DarkMode /> : <LightMode sx={{ color: "#757575" }} />}
					<Text variant="h6">
						{theme.palette.mode === "dark"
							? router.locale === "ar-AE"
								? "التبديل إلى الوضع الفاتح"
								: router.locale === "kn-IN"
									? "ಲೈಟ್ ಮೋಡ್‌ಗೆ ಬದಲಾಯಿಸಿ"
									: "Switch to Light Mode"
							: router.locale === "ar-AE"
								? "التبديل إلى الوضع المظلم"
								: router.locale === "kn-IN"
									? "ಡಾರ್ಕ್ ಮೋಡ್‌ಗೆ ಬದಲಾಯಿಸಿ"
									: "Switch to Dark Mode"}
					</Text>
					<ColorModeContext.Consumer>
						{({ toggleColorMode }) => (
							<FormControlLabel
								onChange={toggleColorMode}
								control={<MaterialUISwitch checked={theme.palette.mode === "dark" ? true : false} />}
								label=""
								labelPlacement="start"
							/>
						)}
					</ColorModeContext.Consumer>
				</ContentContainer>
			) : null}

			{/* Languages */}

			<ContentContainer
				sx={{
					padding: "1.25rem 1rem 0rem 0.8rem",
					borderTop: "1px solid #E0E0E0",
				}}
			>
				<Accordion
					expanded={expandedLanguage}
					onChange={() => setExpandedLanguage(!expandedLanguage)}
					sx={{
						width: "100%",
						background: theme.palette.background.paper,
						padding: "0rem",
						margin: "0rem",
						border: "none",
					}}
				>
					<AccordionSummary
						aria-controls="panel1d-content"
						id="panel1d-header"
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							<Language
								sx={theme.palette.mode === "dark" ? { color: "#FFFFFF" } : { color: "#FC8019" }}
								fontSize="small"
							/>
							<Text sx={{ padding: router.locale === "ar-AE" ? "0rem 0.5rem 0rem 0rem" : "0rem 0rem 0rem 0.5rem" }}>
								{router.locale === "ar-AE" ? "العربية" : router.locale === "kn-IN" ? "ಕನ್ನಡ" : "English"}
							</Text>
						</div>
					</AccordionSummary>
					<AccordionDetails>
						<RadioGroup
							aria-labelledby="your-profiles"
							name="your-profiles"
							sx={{
								// margin: "0.5rem 0rem 0rem 0.5rem",
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-end",
								alignItems: "center",
								width: "100%",
							}}
							value={radioLanguage}
							onChange={(e) => {
								setRadioLanguage(e.target.value);
							}}
						>
							<CustomFormControlLabel
								value="en-IN"
								control={<Radio size="small" />}
								label={<Heading>English</Heading>}
								onClick={() => handleLanguageUrl("en-IN")}
								sx={{
									padding: "0.5rem 0rem 0.5rem 0rem",
									justifyContent: "space-between",
									width: "100%",
								}}
								labelPlacement="start"
							/>
							<CustomFormControlLabel
								value="kn-IN"
								control={<Radio size="small" />}
								label={<Heading>ಕನ್ನಡ</Heading>}
								onClick={() => handleLanguageUrl("kn-IN")}
								sx={{
									padding: "0rem 0rem 0rem 0rem",
									justifyContent: "space-between",
									width: "100%",
								}}
								labelPlacement="start"
							/>
							<CustomFormControlLabel
								value="ar-AE"
								control={<Radio size="small" />}
								label={<Heading>العربية</Heading>}
								onClick={() => handleLanguageUrl("ar-AE")}
								sx={{ padding: "0.5rem 0rem 0.5rem 0rem", justifyContent: "space-between", width: "100%" }}
								labelPlacement="start"
							/>
						</RadioGroup>
					</AccordionDetails>
				</Accordion>
			</ContentContainer>

			{/* Currency */}

			<ContentContainer
				sx={{
					padding: "1.25rem 1rem 0rem 0.8rem",
					// borderTop: "1px solid #E0E0E0",
				}}
			>
				<Accordion
					expanded={expandedCurrency}
					onChange={() => setExpandedCurrency(!expandedCurrency)}
					sx={{
						width: "100%",
						background: theme.palette.background.paper,
						padding: "0rem",
						margin: "0rem",
						border: "none",
					}}
				>
					<AccordionSummary
						aria-controls="panel1d-content"
						id="panel1d-header"
					>
						<div style={{ display: "flex", alignItems: "center" }}>
						<div
							style={{
								width: "20px",
								height: "20px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Typography
								component="span"
								sx={theme.palette.mode === "dark" ? { color: "#FFFFFF" } : { color: "#FC8019" }}
								style={{
									fontSize: "20px",
									lineHeight: 1,
									fontFamily:
										session?.user?.dbUser?.settings?.site_preferences?.default_currency === "aed"
											? "CurrencySymbols"
											: "inherit",
								}}
							>
								{session?.user?.dbUser?.settings?.site_preferences?.default_currency === "aed"
									? String.fromCharCode(0xe001)
									: "₹"}
							</Typography>
						</div>
						<Text sx={{ padding: router.locale === "ar-AE" ? "0rem 0.5rem 0rem 0rem" : "0rem 0rem 0rem 0.5rem" }}>
							{session?.user?.dbUser?.settings?.site_preferences?.default_currency === "aed" ? "AED" : "INR"}
						</Text>
					</div>
					</AccordionSummary>
					<AccordionDetails>
						<RadioGroup
							aria-labelledby="your-profiles"
							name="your-profiles"
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-end",
								alignItems: "center",
								width: "100%",
							}}
							value={session?.user?.dbUser?.settings?.site_preferences?.default_currency || "inr"}
							onChange={async (e) => {
								await dispatch(updateCurrencyThunk({ currency: e.target.value, userId: profile_context.user_id }));
							}}
						>
							<CustomFormControlLabel
								value="inr"
								control={<Radio size="small" />}
								label={
									<Heading>
										<span
											style={{
												marginRight: "0.25rem",
												// fontWeight: "bold",
											}}
										>
											₹
										</span>
										INR
									</Heading>
								}
								onClick={async () => {
									await dispatch(updateCurrencyThunk({ currency: "inr", userId: profile_context.user_id }));
									setExpandedCurrency(!expandedCurrency);
								}}
								sx={{
									padding: "0.5rem 0rem 0.5rem 0rem",
									justifyContent: "space-between",
									width: "100%",
								}}
								labelPlacement="start"
							/>
							<CustomFormControlLabel
								value="aed"
								control={<Radio size="small" />}
								label={
									<Heading>
										<span
											style={{
												marginRight: "0.25rem",
												// fontWeight: "bold",
												fontFamily: "CurrencySymbols",
											}}
										>
											{String.fromCharCode(0xe001)}
										</span>
										AED
									</Heading>
								}
								onClick={async () => {
									await dispatch(updateCurrencyThunk({ currency: "aed", userId: profile_context.user_id }));
									setExpandedCurrency(!expandedCurrency);
								}}
								sx={{
									padding: "0rem 0rem 0rem 0rem",
									justifyContent: "space-between",
									width: "100%",
								}}
								labelPlacement="start"
							/>
						</RadioGroup>
					</AccordionDetails>
				</Accordion>
			</ContentContainer>

			{/* Settings */}

			<Links
				href={`${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
					`${process.env.PRODUCTION_URL || ""}/` + account_settings_url,
				)}&locale=${router.locale || "en-IN"}`}
				rel="noopener"
				target="_self"
				referrerPolicy="no-referrer"
			>
				<ContentContainer
					sx={{
						cursor: "pointer",
						padding: "1.25rem 1rem 0rem 1rem",
					}}
				>
					<ContentRow>
						<SettingsOutlined sx={theme.palette.mode === "dark" ? { color: "#FFFFFF" } : { color: "#FC8019" }} />
						<Text variant="h6">
							{router.locale === "ar-AE" ? "الإعدادات" : router.locale === "kn-IN" ? "ಸೆಟ್ಟಿಂಗ್‌ಗಳು" : "Settings"}
						</Text>
					</ContentRow>
				</ContentContainer>
			</Links>

			{/* FAQs */}

			<Links
				href={`${process.env.PRODUCTION_URL}loading.html?callbackUrl=${encodeURIComponent(`/${router.locale}/faqs`)}`}
				rel="noopener"
				target="_self"
				referrerPolicy="no-referrer"
			>
				<ContentContainer
					sx={{
						cursor: "pointer",
						padding: "1.25rem 1rem 0rem 1rem",
					}}
				>
					<ContentRow>
						<LiveHelpOutlined sx={theme.palette.mode === "dark" ? { color: "#FFFFFF" } : { color: "#FC8019" }} />
						<Text variant="h6">
							{router.locale === "ar-AE" ? "الأسئلة الشائعة" : router.locale === "kn-IN" ? "ಎಫ್ಎಕ್ಯೂ" : "FAQ's"}
						</Text>
					</ContentRow>
				</ContentContainer>
			</Links>

			{/* Logout */}

			<ContentContainer
				sx={{
					cursor: "pointer",
					padding: "1.25rem 1rem 1.25rem 1rem",
				}}
				onClick={() => {
					//? Drop the acting business and session at once, then end the server session
					//? and reload into the logged-out page (same flow as ProfileDropdown). A
					//? no-redirect signOut left the sessions row valid and the page still loading data.
					dispatch(profileContextThunk(emptyProfileContext));
					dispatch(updateReduxSessionThunk(null as any));
					signOut({
						callbackUrl:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
					});
				}}
			>
				<ContentRow sx={{ padding: "0rem 0rem 0rem 0rem" }}>
					<Logout sx={theme.palette.mode === "dark" ? { color: "#FFFFFF" } : { color: "#FC8019" }} />
					<Text variant="h6">
						{router.locale === "ar-AE" ? "تسجيل الخروج" : router.locale === "kn-IN" ? "ಲಾಗೌಟ್" : "Logout"}
					</Text>
				</ContentRow>
			</ContentContainer>
		</React.Fragment>
	);
};

export default UserContent;
